import * as React from 'react'

import { CloseIcon } from '~/client/src/shared/components/icons/CloseIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

interface IProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'className'
> {
  children: React.ReactNode
  icon?: React.ReactNode
  avatar?: React.ReactNode
  trailing?: React.ReactNode
  onDismiss?: () => void
  disabled?: boolean
  className?: string
}

const BASE = [
  'tw:inline-flex tw:items-center tw:gap-0.5',
  'tw:rounded-sm tw:px-0.5',
  'tw:bg-muted',
  'tw:text-foreground tw:text-label-lg',
  // max-w-full caps a tag at its container so a long label ellipsises inside the tag
  // instead of overflowing it - which in a clipped table cell used to cut the label off
  // mid-word AND take the dismiss button with it. shrink-0 still keeps tags from
  // squashing each other; they wrap instead.
  'tw:select-none tw:shrink-0 tw:max-w-full',
].join(' ')

// A clickable tag (e.g. edit-this-item in a tag field) gets its own hover, so it
// reads as a separate target from the field around it. Its dismiss icon darkens
// with the whole chip, not only when the icon itself is hovered.
const CLICKABLE = 'tw:group/tag tw:cursor-pointer tw:hover:bg-accent'

// shrink-0 on both affordances, so when the tag hits its max width the LABEL is the only
// thing that gives way. Without it the icon and the dismiss button compress first and the
// "x" disappears - the thing a truncated tag most needs to keep.
const ICON =
  'tw:size-[18px] tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:text-muted-foreground'

const AVATAR = 'tw:flex tw:items-center tw:justify-center tw:shrink-0'

const DISMISS = [
  'tw:inline-flex tw:items-center tw:justify-center tw:shrink-0',
  'tw:rounded-sm tw:size-[18px] tw:p-0',
  'tw:border-none tw:bg-transparent',
  'tw:text-muted-foreground tw:cursor-pointer',
  'tw:hover:text-foreground tw:group-hover/tag:text-foreground',
].join(' ')

export function Tag({
  children,
  icon,
  avatar,
  trailing,
  onDismiss,
  disabled = false,
  className,
  ...rest
}: IProps) {
  const leading = avatar ?? icon

  return (
    <span
      className={cn({
        [BASE]: true,
        [CLICKABLE]: !!rest.onClick,
        'tw:opacity-[0.38] tw:pointer-events-none': disabled,
        [className]: !!className,
      })}
      {...rest}
    >
      {leading && <span className={avatar ? AVATAR : ICON}>{leading}</span>}
      {/* min-w-0 so the label can shrink past its content, and the ellipsis lands on
          the box that holds the text — inert unless a consumer constrains the Tag. */}
      <span className="tw:min-w-0 tw:truncate">{children}</span>
      {trailing}
      {onDismiss && (
        <button
          type="button"
          aria-label={Localization.translator.remove}
          className={DISMISS}
          onClick={e => {
            e.stopPropagation()
            onDismiss()
          }}
        >
          <CloseIcon size="xs" />
        </button>
      )}
    </span>
  )
}
